import useAccess from '@/composables/access/access';
import useGridPage from '@/composables/grid-page/grid-page';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePlannings from '@/composables/planning/plannings';
import { accessGroupFlag, accessPolicyType, viewApplication as viewApplicationConfig } from 'shared/constants.json';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export default function useViewsContext(viewApplication, readOnly = false) {
  const route = useRoute();
  const { spacesWithWriteAccessIds } = useLoggedInUser();
  const { userHasRight } = useAccess();
  const hasPublicViewAccess = computed(() => userHasRight([accessGroupFlag.publicSavedViews]));
  const gridPageSvc = useGridPage();
  const planningsSvc = usePlannings();

  const isPublic = computed(() => [
    viewApplicationConfig.goal,
    viewApplicationConfig.update,
    viewApplicationConfig.updateFeed,
    viewApplicationConfig.insight,
    viewApplicationConfig.insightHealth,
    viewApplicationConfig.goalSpace,
    viewApplicationConfig.updateSpace,
    viewApplicationConfig.updateFeedSpace,
    viewApplicationConfig.insightSpace,
    viewApplicationConfig.memberSpace,
    viewApplicationConfig.gridPage,
    viewApplicationConfig.gridPageTileCheckin,
    viewApplicationConfig.gridPageTileGoal,
    viewApplicationConfig.gridPageTileUpdateFeed,
    viewApplicationConfig.gridPageTileMember,
    viewApplicationConfig.planningDetails,
  ].includes(viewApplication));

  const canEdit = computed(() => {
    if (readOnly) {
      return false;
    }

    if (viewApplication === viewApplicationConfig.memberSpace
      || viewApplication.includes('temp_goal_list')
    ) {
      return false;
    }

    if (isPublic.value === false) {
      return true;
    }

    if (hasPublicViewAccess.value) {
      return true;
    }

    if ([viewApplicationConfig.planningDetails].includes(viewApplication)) {
      const planningId = parseInt(route.params.id, 10);
      return [accessPolicyType.write, accessPolicyType.full].includes(planningsSvc.selectSingle(planningId)?.accessRight);
    }

    if ([viewApplicationConfig.gridPage,
      viewApplicationConfig.gridPageTileGoal,
      viewApplicationConfig.gridPageTileUpdateFeed,
      viewApplicationConfig.gridPageTileMember,
      viewApplicationConfig.gridPageTileCheckin,
    ].includes(viewApplication)) {
      const gridPageId = parseInt(route.params.pageId, 10);
      return [accessPolicyType.write, accessPolicyType.full].includes(gridPageSvc.selectSingleGridPage(gridPageId)?.accessRight);
    }

    if ([viewApplicationConfig.goalSpace,
      viewApplicationConfig.updateSpace,
      viewApplicationConfig.updateFeedSpace,
      viewApplicationConfig.insightSpace,
      viewApplicationConfig.memberSpace,
    ].includes(viewApplication)) {
      const spaceId = parseInt(route.params.optionId, 10);
      return spacesWithWriteAccessIds.value.includes(spaceId);
    }

    return false;
  });

  const ro = computed(() => readOnly);

  return { canEdit, isPublic, readOnly: ro };
}
